import { createTheme, darken, alpha } from '@mui/material/styles';
import extend from 'lodash/merge';
import * as TIC from './tic';
import * as TMG from './tmg';
import * as HITCHED from './hitched';
import shadows from './shadow';

const THEME = {
  TIC,
  TMG,
  HITCHED,
};

const greyDark = {
  100: '#192231',
  200: '#303846',
  300: '#474e5a',
  400: '#5e646f',
  500: '#757a83',
  600: '#8c9198',
  700: '#a3a7ad',
  800: '#babdc1',
};

const greyLight = {
  100: '#F7F8FC',
  200: '#E9E9F2',
  300: '#E4E7EB',
  400: '#CBD2D9',
  500: '#9AA5B1',
  600: '#616E7C',
  700: '#52606D',
  800: '#3E4C59',
};

export default (brand, mode = 'light', overridePallets) => {
  const isDark = mode === 'dark';
  const themePalletes = isDark ? THEME[brand].DARK : THEME[brand].LIGHT;
  const palettes = extend({}, themePalletes, overridePallets);

  const grey = isDark ? greyDark : greyLight;
  const theme = createTheme({
    shadows: isDark ? [] : shadows,
    palette: {
      mode,
      text: {
        primary: palettes.text,
        secondary: palettes.textSecondary,
      },
      default: {
        main: palettes.text,
      },
      primary: {
        main: palettes.primary,
        darker: darken(palettes.primary, 0.1),
        light: alpha(palettes.primary, 0.2),
        lighter: '#E6F3FF',
      },
      secondary: {
        main: palettes.secondary,
        contrastText: '#fff',
      },
      grayed: {
        main: grey[800],
        contrastText: grey[800],
      },
      todo: {
        main: isDark ? greyLight[100] : '#72889D',
        contrastText: isDark ? greyDark[200] : '#fff',
      },
      success: {
        main: '#2AC184',
        light: '#3DD598',
        lighter: '#EAF9F3',
        contrastText: '#fff',
      },
      info: {
        dark: '#7D56E5',
        main: '#895FFC',
        light: '#C9B5FE',
        lighter: '#F3EFFF',
      },
      error: {
        dark: '#E55252',
        main: '#FC5A5A',
        light: '#FD7B7B',
        lighter: '#FFEFEF',
      },
      warning: {
        main: '#E8632A',
        light: '#FDD687',
        lighter: '#FFF0EA',
      },
      pending: {
        main: '#FFD66A',
        contrastText: '#fff',
      },
      background: {
        default: grey['100'],
        paper: palettes.paper,
        navbar: palettes.navbar,
        cta: palettes.cta,
      },
      cta: {
        main: palettes.cta,
        contrastText: '#fff',
      },
      neutral: {
        main: palettes.paper,
        contrastText: palettes.text,
      },
      inverse: {
        main: palettes.paper,
        contrastText: palettes.primary,
      },
      divider: grey['200'],
      action: {
        selected: alpha(palettes.primary, 0.08),
      },
      talk: {
        dark: '#6143B3',
        main: '#895FFC',
        light: '#7D56E5',
        lighter: '#F3EFFF',
        contrastText: '#fff',
      },
      note: {
        dark: '#E73DAF',
        main: '#FE43C0',
        contrastText: '#fff',
        light: '#FFECF9',
        lighter: '#FFECF9',
      },
      grey,
    },
    shape: {
      borderRadius: 12,
    },
    typography: {
      fontFamily: 'Poppins, sans-serif',
      h1: {
        fontSize: 30,
        fontWeight: 600,
        lineHeight: '36px',
      },
      h2: {
        fontSize: 24,
        lineHeight: '28px',
        fontWeight: 600,
      },
      h3: {
        fontSize: 20,
        lineHeight: '24px',
        fontWeight: 600,
      },
      h4: {
        fontSize: 18,
        lineHeight: '20px',
        fontWeight: 600,
      },
      h5: {
        fontSize: 12,
        lineHeight: '17px',
        fontWeight: 400,
      },
      button: {
        textTransform: 'none',
      },
      body1: {
        fontSize: 14,
      },
      body2: {
        fontSize: 12,
      },
      caption: {
        fontSize: 12,
        lineHeight: 1.4,
      },
      small: {
        fontSize: 10,
      },
    },
    components: {
      MuiAccordion: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
          rounded: {},
        },
      },
      MuiButtonBase: {
        defaultProps: {
          disableTouchRipple: true,
        },
      },
      MuiIconButton: {
        variants: [
          {
            props: {
              active: true,
            },
            style: {
              backgroundColor: alpha(palettes.primary, 0.1),
            },
          },
        ],
      },
      MuiTypography: {
        defaultProps: {
          color: 'textPrimary',
        },
      },
      MuiTooltip: {
        defaultProps: {
          arrow: true,
        },
      },
      MuiButton: {
        variants: [
          {
            props: {
              variant: 'link',
            },
            style: {
              'padding': 0,
              'display': 'inline-flex',
              'textAlign': 'initial',
              'color': palettes.primary,
              'minWidth': 'unset',
              'fontWeight': 'inherit',
              'fontSize': 'inherit',
              'lineHeight': 1.5,
              'verticalAlign': 'inherit',
              '&:hover': {
                background: 'none',
                color: darken(palettes.primary, 0.3),
              },
              '&:focus': {
                outline: 'none',
              },
            },
          },
          {
            props: {
              color: 'cta',
            },
            style: {
              'backgroundColor': palettes.cta,
              '&:hover': {
                backgroundColor: darken(palettes.cta, 0.1),
              },
            },
          },
          {
            props: {
              rounded: true,
            },
            style: {
              borderRadius: 30,
            },
          },
          {
            props: {
              size: 'small',
              rounded: true,
            },
            style: {
              borderRadius: 12,
            },
          },
          {
            props: {
              size: 'small',
            },
            style: {
              lineHeight: 1.5,
            },
          },
          {
            props: {
              color: 'neutral',
            },
            style: ({ theme }) => ({
              'border': '1px solid',
              'borderColor': grey['300'],
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }),
          },
        ],
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          sizeMedium: {
            padding: '7px 16px',
          },
          outlinedSizeMedium: {
            padding: '6px 15px',
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            'borderColor': grey['300'],
            '&.Mui-selected': {
              'backgroundColor': theme.palette.primary.lighter,
              'color': theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.light,
              },
            },
          }),
        },
      },
      MuiCard: {
        defaultProps: {
          elevation: 3,
        },
        styleOverrides: {
          root: {
            overflow: 'inherit',
          },
        },
        variants: [
          {
            props: { variant: 'outlinedVariant' },
            style: {
              border: '1px solid',
              borderColor: grey['300'],
              backgroundColor: grey['100'],
            },
          },
          {
            props: { selected: true },
            style: ({ theme }) => ({
              borderColor: theme.palette.primary.main,
              backgroundColor: theme.palette.primary.lighter,
              color: theme.palette.primary.main,
            }),
          },
        ],
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            paddingLeft: 24,
            paddingRight: 24,
            paddingBottom: 16,
          },
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 3,
        },
        styleOverrides: {
          rounded: {
            borderRadius: 12,
          },
        },
      },
      MuiInput: {
        defaultProps: {},
      },
      MuiInputBase: {
        variants: [
          {
            props: {
              size: 'compact',
            },
            style: {
              height: 30,
              input: {
                padding: '4px 8px',
              },
            },
          },
        ],
      },
      MuiTextField: {
        defaultProps: {
          size: 'small',
          margin: 'normal',
          variant: 'outlined',
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
          multiline: {
            borderRadius: 12,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          colorInherit: {
            backgroundColor: palettes.navbar,
            color: '#fff',
          },
          colorDefault: {
            backgroundColor: '#fff',
          },
        },
      },
      MuiAvatar: {
        variants: [
          {
            props: {
              variant: 'outlined',
            },
            style: {
              border: `1px solid ${grey['300']}`,
              backgroundColor: palettes.paper,
            },
          },
        ],
        styleOverrides: {
          colorDefault: {
            backgroundColor: grey['500'],
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: 32,
          },
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            fontSize: 14,
          },
        },
      },
      MuiSwitch: {
        variants: [
          {
            props: {
              size: 'small',
            },
            style: {
              'width': 44,
              'height': 32,
              'padding': 7,
              '& .MuiSwitch-switchBase': {
                padding: 8,
              },
            },
          },
        ],
        styleOverrides: {
          root: {
            padding: 7,
          },
          thumb: {
            width: 24,
            height: 24,
            backgroundColor: '#fff',
            boxShadow:
              '0 0 2px 0 rgba(0,0,0,0.08), 0 0 2px 0 rgba(0,0,0,0.12), 0 0 2px 0 rgba(0,0,0,0.38)',
          },
          switchBase: {
            'color': 'rgba(0,0,0,0.38)',
            'padding': 7,
            '&.Mui-checked': {
              'color': '#fff',
              '& + .MuiSwitch-track': {
                backgroundColor: isDark ? '#2ECA45' : '#65C466',
                opacity: 1,
                border: 0,
              },
              '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.5,
              },
            },
          },
          track: {
            borderRadius: 20,
          },
          focusVisible: {},
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            'fontWeight': 500,
            'paddingLeft': 16,
            'color': palettes.text,
            '&.Mui-focused:not(.Mui-error)': {
              color: palettes.text,
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&:hover': {
              color: palettes.primary,
            },
          },
        },
      },
      MuiTabs: {
        variants: [
          {
            props: { size: 'small' },
            style: {
              'minHeight': 36,
              '& .MuiButtonBase-root.MuiTab-root': {
                padding: '0 8px',
                minHeight: 36,
                minWidth: 50,
                fontSize: 13,
              },
            },
          },
        ],
      },
      MuiTable: {
        variants: [
          {
            props: { variant: 'standard' },
            style: {
              '& tr.spacer': {
                display: 'none',
              },
            },
          },
          {
            props: { variant: 'card' },
            style: {
              '& .MuiTableRow-hover:hover': {
                backgroundColor: 'transparent !important',
              },
              'borderCollapse': 'separate',
              '& thead th': {
                border: 'none',
              },
              '& tbody tr:hover': {
                td: {
                  backgroundColor: alpha(grey[400], 0.24),
                },
              },
              '& tbody td': {
                'border': 0,
                'borderBottom': `1px solid ${grey['300']}`,
                'borderTop': `1px solid ${grey['300']}`,
                'backgroundColor': grey['100'],
                '&:first-of-type': {
                  borderRadius: '8px 0 0 8px',
                  borderLeft: `1px solid ${grey['300']}`,
                  overflow: 'hidden',
                },
                '&:last-of-type': {
                  borderRadius: '0 8px 8px 0',
                  borderRight: `1px solid ${grey['300']}`,
                  overflow: 'hidden',
                },
              },
              '& tbody .child-row:not(.child-row--first):not(.child-row--last) td:first-of-type, & tbody .child-row:not(.child-row--first):not(.child-row--last) td:last-of-type':
                { borderRadius: 'unset' },
              '& tbody .child-row--first.child-row--last td:first-of-type': {
                borderBottomLeftRadius: 8,
              },
              '& tbody .child-row:not(.child-row--last) td': {
                borderTop: 'none',
                borderBottom: 'none',
              },
            },
          },
        ],
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottomColor: grey['300'],
            fontSize: 14,
          },
          head: {
            fontSize: 12,
            fontWeight: 600,
            textTransform: 'uppercase',
            color: '#A1A2B2',
          },
        },
      },
      MuiLink: {
        defaultProps: {
          underline: 'none',
        },
      },
      MuiBadge: {
        variants: [
          {
            props: {
              variant: 'outlined',
              color: 'primary',
            },
            style: {
              '> .MuiBadge-badge': {
                backgroundColor: alpha(palettes.primary, 0.1),
                color: palettes.primary,
              },
            },
          },
          {
            props: {
              size: 'small',
            },
            style: {
              '> .MuiBadge-badge': {
                minWidth: 16,
                height: 16,
                fontSize: 12,
                fontWeight: 400,
              },
            },
          },
        ],
      },
      MuiAlert: {
        styleOverrides: {
          outlinedInfo: ({ theme }) => ({
            backgroundColor: theme.palette.info.lighter,
            color: theme.palette.info.dark,
            borderColor: theme.palette.info.light,
          }),
        },
      },
      MuiChip: {
        variants: [
          {
            props: { rounded: true },
            style: ({ ownerState: { size, icon } }) => {
              return {
                borderRadius: size === 'small' ? 16 : 24,
                paddingLeft: icon ? 8 : 0,
              };
            },
          },
        ],
        styleOverrides: {
          root: {
            borderRadius: 5,
          },
          outlinedPrimary: {
            backgroundColor: alpha(palettes.primary, 0.1),
            color: palettes.primary,
          },
          outlinedInfo: {
            backgroundColor: alpha('#9747FF', 0.1),
            color: '#9747FF',
            borderColor: '#9747FF',
          },
          outlinedError: {
            backgroundColor: alpha('#FC5959', 0.1),
            color: '#FC5959',
          },
          outlinedWarning: {
            backgroundColor: alpha('#DBAB07', 0.1),
            color: '#B08A00',
          },
          outlinedSuccess: {
            backgroundColor: alpha('#2ECA45', 0.1),
            color: '#2ECA45',
          },
          outlinedNeutral: {
            backgroundColor: themePalletes.paper,
            color: themePalletes.text,
            borderColor: grey['400'],
          },
          sizeSmall: {
            fontSize: 12,
          },
        },
      },
      MuiTableSortLabel: {
        styleOverrides: {
          root: {
            'fontSize': 12,
            'fontWeight': 600,
            'textTransform': 'uppercase',
            'color': '#A1A2B2',
            '&.Mui-active': {
              '& .MuiTableSortLabel-icon': {
                color: palettes.text,
              },
            },
          },
          icon: {
            opacity: 0.4,
          },
        },
      },
    },
  });

  return theme;
};
